// AccordionExtension.tsx
import { findParentNodeOfType, NodeExtension } from '@remirror/core';
import { NodeSelection, TextSelection } from '@remirror/pm/state';
import { useRemirrorContext } from '@remirror/react';
import React, { useEffect, useState } from 'react';
import './AccordionExtension.css';

// AccordionComponent.tsx

const getParentAccordion = ({ state, getPosition }) => {
  const pos = getPosition?.();
  if (pos == null) return {};

  const parent = findParentNodeOfType({
    selection: state.selection.$head,
    types: ['accordion'],
  });
  if (!parent) return {};

  const { node, pos: parentPos } = parent;
  return { node, parentPos };
};

export const AccordionTitleComponent = ({ forwardRef, getPosition, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { view } = useRemirrorContext();

  useEffect(() => {
    const { node: parentNode } = getParentAccordion({
      state: view.state,
      getPosition,
    });
    if (!parentNode) return;
    setIsOpen(parentNode.attrs.open);
  }, [view.state, getPosition]);

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { state, dispatch } = view;
    const parent = getParentAccordion({ state, getPosition });
    if (!parent) return;

    const { node, parentPos } = parent;
    const isOpen = node.attrs.open;
    setIsOpen(!isOpen);
    dispatch(
      state.tr.setNodeMarkup(parentPos, undefined, {
        ...node.attrs,
        open: !isOpen,
      })
    );
  };

  return (
    <div data-accordion-title className="accordion-title">
      <div className="accordion-title-icon" onClick={handleClick}>
        {isOpen ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        )}
      </div>
      <div className="accordion-title-content" ref={forwardRef} style={{ width: '100%' }}>
        {children}
      </div>
    </div>
  );
};

export const AccordionContentComponent = ({ forwardRef, children }) => {
  return (
    <div ref={forwardRef} data-accordion-content className="accordion-content">
      {children}
    </div>
  );
};

export class AccordionTitleExtension extends NodeExtension {
  static disableExtraAttributes = true;

  get name() {
    return 'accordionTitle';
  }

  createNodeSpec() {
    return {
      content: 'inline*',
      group: 'block',
      defining: true,
      parseDOM: [{ tag: 'div[data-accordion-title]' }],
      toDOM: () => [
        'div',
        {
          'data-accordion-title': '',
          'data-accordion-placeholder': 'Give this expand a title...',
        },
        0,
      ],
    };
  }

  createKeymap() {
    const backspaceHandler = ({ state, dispatch }) => {
      const { selection } = state;
      const isNodeSelection = selection instanceof NodeSelection;
      const { $from } = selection;

      const titleNode = findParentNodeOfType({
        selection: selection.$head,
        types: ['accordionTitle'],
      });
      if (
        (isNodeSelection && selection.node.type.name !== 'accordionTitle') ||
        (!isNodeSelection && (!titleNode || titleNode?.node?.type?.name !== 'accordionTitle'))
      )
        return false;

      const isEmptyTextBlock =
        selection instanceof TextSelection && $from.parentOffset === 0 && $from.parent.content.size === 0;

      const isEmptyNodeSelection = isNodeSelection && selection.node.type.name === 'accordionTitle';
      if (!isEmptyTextBlock && !isEmptyNodeSelection) return false;

      const accordionPos = findParentNodeOfType({
        selection,
        types: ['accordion'],
      });
      if (!accordionPos) return false;

      const { pos } = accordionPos;
      if (dispatch) {
        dispatch(state.tr.delete(pos, pos + accordionPos.node.nodeSize).scrollIntoView());
      }

      return true;
    };

    const enterHandler = ({ state, dispatch }) => {
      const { selection, doc } = state;
      const accordion = findParentNodeOfType({
        selection,
        types: ['accordion'],
      });
      if (!accordion) return false;
      const accordionNode = accordion.node;
      const accordionPos = accordion.pos;
      const titleNode = accordionNode.child(0);

      const titleSelection = findParentNodeOfType({
        selection,
        types: ['accordionTitle'],
      });
      const isInTitle = titleSelection && titleSelection.node.type.name === 'accordionTitle';
      if (!isInTitle) return false;

      const offsetToContent = titleNode.nodeSize;
      const contentStartPos = accordionPos + 1 + offsetToContent + 1;

      if (dispatch) {
        const resolved = doc.resolve(contentStartPos);
        // Double check: if resolved is inside contentNode
        if (resolved.parent.type.name === 'accordionContent' || resolved.nodeAfter?.type.name === 'paragraph') {
          const tr = state.tr
            .setSelection(TextSelection.near(resolved))
            .scrollIntoView()
            .setMeta('addToHistory', false);
          dispatch(tr);

          return true;
        }
      }

      return false;
    };

    return {
      Backspace: backspaceHandler,
      Enter: enterHandler,
    };
  }
}

export class AccordionContentExtension extends NodeExtension {
  static disableExtraAttributes = true;

  get name() {
    return 'accordionContent';
  }

  createNodeSpec() {
    return {
      content: 'paragraph',
      group: 'block',
      defining: true,
      parseDOM: [{ tag: 'div[data-accordion-content]' }],
      toDOM: () => [
        'div',
        {
          'data-accordion-content': '',
          style: "--data-accordion-placeholder: 'Give this expand a content...'",
          'data-accordion-placeholder': 'Give this expand a content...',
        },
        0,
      ],
    };
  }

  createKeymap() {
    const backspaceHandler = ({ state, dispatch }) => {
      const { selection } = state;
      // Ensure selection is at start of an empty accordionContent
      const $from = selection.$from;
      const parentNode = findParentNodeOfType({
        selection,
        types: ['accordion'],
      });
      if (!parentNode) return false;

      const { node: accordionNode, pos: accordionPos } = parentNode;
      const titleNode = accordionNode.child(0);
      const contentNode = accordionNode.child(1);

      const contentSelection = findParentNodeOfType({
        selection,
        types: ['accordionContent'],
      });
      const isInContent = contentSelection && contentSelection.node.type.name === 'accordionContent';
      const isEmptyContent = contentNode.content.size === 2;
      const isAtStartOfContent = selection.empty && $from.parentOffset === 0;

      if (isInContent && isEmptyContent && isAtStartOfContent) {
        const titleStartPos = accordionPos + 1;
        const titleEndPos = titleStartPos + titleNode.content.size + 1;

        const tr = state.tr.setSelection(TextSelection.create(state.doc, titleEndPos)).scrollIntoView();
        dispatch(tr);
        return true;
      }

      return false;
    };

    const enterHandler = ({ state, dispatch }) => {
      const { selection } = state;
      const $pos = selection.$from;

      const accordion = findParentNodeOfType({
        selection,
        types: ['accordion'],
      });
      if (!accordion) return false;

      const { node: accordionNode, pos: accordionPos } = accordion;
      const titleNode = accordionNode.child(0);
      const contentNode = accordionNode.child(1);

      const contentSelection = findParentNodeOfType({
        selection,
        types: ['accordionContent'],
      });
      const isInContent = contentSelection && contentSelection.node?.type?.name === 'accordionContent';
      if (!isInContent) return false;

      const contentEnd = accordionPos + 1 + titleNode.nodeSize + contentNode.nodeSize - 2;
      const isAtEndOfContent = selection.empty && $pos.pos === contentEnd;
      if (!isAtEndOfContent) return false;

      if (dispatch) {
        const paragraph = state.schema.nodes.paragraph.create();
        const tr = state.tr.insert(accordionPos + accordionNode.nodeSize, paragraph);
        const resolved = tr.doc.resolve(accordionPos + accordionNode.nodeSize + 1);
        dispatch(tr.setSelection(TextSelection.near(resolved)).scrollIntoView());
      }

      return true;
    };

    return {
      Backspace: backspaceHandler,
      Enter: enterHandler,
    };
  }
}

export class AccordionExtension extends NodeExtension {
  static disableExtraAttributes = true;

  get name() {
    return 'accordion';
  }

  createNodeSpec() {
    return {
      content: 'accordionTitle accordionContent',
      group: 'block',
      isolating: true,
      defining: true,
      attrs: {
        open: { default: false },
      },
      parseDOM: [
        {
          tag: 'div[data-accordion]',
          getAttrs: (dom) => ({
            open: dom.getAttribute('data-open') === 'true',
          }),
        },
      ],
      toDOM: (node) => [
        'div',
        {
          'data-accordion': '',
          'data-open': node.attrs.open,
          class: 'accordion',
        },
        0,
      ],
    };
  }

  createCommands() {
    return {
      insertAccordion:
        () =>
        ({ tr, state, dispatch }) => {
          const { schema, selection } = state;
          const listParent = findParentNodeOfType({
            selection,
            types: [
              schema.nodes.bulletList,
              schema.nodes.orderList,
              schema.nodes.taskList,
              schema.nodes.taskListItem,
              schema.nodes.listItem,
            ],
          });

          if (listParent) {
            // You can show a toast, log, or just silently return
            console.warn('Cannot insert accordion inside a list.');
            return false;
          }

          const title = schema.nodes.accordionTitle.create({}, null);
          const paragraph = schema.nodes.paragraph.createAndFill();
          const content = schema.nodes.accordionContent.create({}, paragraph);
          const accordionNode = schema.nodes.accordion.create({ open: true }, [title, content]);
          const insertPos = tr.selection.from;
          tr = tr.replaceSelectionWith(accordionNode).scrollIntoView();

          const resolved = tr.doc.resolve(insertPos + 1); // Position inside title
          tr = tr.setSelection(TextSelection.near(resolved));

          dispatch?.(tr);
          return true;
        },
    };
  }
}
